import React, { useState, useEffect } from "react";
import { BsPlusSquareFill } from "react-icons/bs";
import { ImCross } from "react-icons/im";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useCurrency } from '../../../context/currencyContext';

function CloseInvoiceCase({ caseData, onInvoiceCreated }) {
  const { currency, formatAmount } = useCurrency();
  const [trackNumber, setTrackNumber] = useState("");
  const [shippingCost, setShippingCost] = useState("0.00");
  const [rescheduleOption, setRescheduleOption] = useState("Ship Today");
  const [invoiceNow, setInvoiceNow] = useState(true);
  const [relatedCase, setRelatedCase] = useState(false);
  const [addShippingCost, setAddShippingCost] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState("Thank you for your business!");
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [status, setStatus] = useState("unpaid");
  const { caseId } = useParams();
  const [units, setUnits] = useState([]);
  const [amounts, setAmounts] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [taxes] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]); // Empty array instead of default item
  const [showModal, setShowModal] = useState(false);
  // Shipping options
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(true);
  // Get userLabId from localStorage
  const [userLabId, setUserLabId] = useState("");

  // Initialize userLabId from localStorage on component mount
  useEffect(() => {
    const storedUserLabId = localStorage.getItem('userLabId');
    if (storedUserLabId) {
      setUserLabId(storedUserLabId);
    } else {
      console.warn("userLabId not found in localStorage");
      // You might want to handle this case differently, e.g., redirect to login
    }
  }, []);

  // Initialize units & amounts from DB
  useEffect(() => {
    if (caseData?.procedures && Array.isArray(caseData.procedures)) {
      const initialUnits = caseData.procedures.map(proc => proc.calculatedUnits || 1);
      const initialAmounts = caseData.procedures.map((proc, i) => {
        const basePrice = Number(proc.price) / initialUnits[i];
        return initialUnits[i] * basePrice;
      });
      setUnits(initialUnits);
      setAmounts(initialAmounts);
    } else {
      setUnits([]);
      setAmounts([]);
    }
  }, [caseData]);

  // Fetch shipping types from backend
  const fetchShippingTypes = async () => {
    setShippingLoading(true);
    try {
      const res = await axios.get("https://hms.backend.hiragroupcloud.com/api/shipping");
      if (res.data.success) {
        let options = res.data.data
          .map(st => ({
            id: st._id,
            label: st.service ? `${st.company} - ${st.service}` : st.company,
            price: Number(st.price) || 0,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        let selected = null;
        let price = 0;
        // Check if case has assigned shipping service
        if (caseData?.shippingService) {
          const comp = caseData.shippingService.company || "";
          const serv = caseData.shippingService.service || "";
          const assignedLabel = serv ? `${comp} - ${serv}` : comp;
          price = Number(caseData.shippingService.price) || 0;
          selected = options.find(o => o.label === assignedLabel);
          if (!selected) {
            selected = { id: `custom-${Date.now()}`, label: assignedLabel, price };
            options.push(selected);
          }
        }
        // If no assigned shipping service, don't auto-select anything
        else {
          selected = null;
          price = 0;
        }
        setShippingOptions(options);
        setSelectedShipping(selected);
        setShippingCost(price.toFixed(2));
        setAddShippingCost(price > 0);
      }
    } catch (err) {
      console.error("Failed to fetch shipping types:", err);
      // On error: only use assigned service if available
      let selected = null;
      let price = 0;
      let options = [];
      if (caseData?.shippingService) {
        const comp = caseData.shippingService.company || "";
        const serv = caseData.shippingService.service || "";
        const label = serv ? `${comp} - ${serv}` : comp;
        price = Number(caseData.shippingService.price) || 0;
        selected = { id: "error-custom", label, price };
        options = [selected];
      }
      setShippingOptions(options);
      setSelectedShipping(selected);
      setShippingCost(price.toFixed(2));
      setAddShippingCost(price > 0);
    } finally {
      setShippingLoading(false);
    }
  };

  useEffect(() => {
    fetchShippingTypes();
  }, []);

  // Recalculate totals
  useEffect(() => {
    const procedureTotal = amounts.reduce((sum, a) => sum + (Number(a) || 0), 0);
    const extraTotal = items.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.amount) || 0), 0);
    const shipping = addShippingCost ? Number(shippingCost) || 0 : 0;
    const newSubtotal = procedureTotal + extraTotal + shipping;
    const newTotal = newSubtotal + taxes;
    setSubtotal(newSubtotal);
    setTotal(newTotal);
  }, [amounts, items, addShippingCost, shippingCost, taxes]);

  // Format delivery date
  const deliveryDateOption = caseData?.delivery_date
    ? new Date(caseData.delivery_date).toISOString().split("T")[0]
    : null;

  const handleAddItem = () => {
    // Add empty item with 0 amount instead of default 200
    setItems([...items, { quantity: 1, amount: 0, description: "" }]);
  };

  const handleUnitChange = (index, value) => {
    const newUnits = [...units];
    const newAmounts = [...amounts];
    const unitCount = Math.max(1, Number(value) || 1);
    newUnits[index] = unitCount;
    const basePrice = Number(caseData.procedures[index].price) / (caseData.procedures[index].calculatedUnits || 1);
    newAmounts[index] = unitCount * basePrice;
    setUnits(newUnits);
    setAmounts(newAmounts);
  };

  const handleAmountChange = (index, value) => {
    const newAmounts = [...amounts];
    newAmounts[index] = Math.max(0, Number(value) || 0);
    setAmounts(newAmounts);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = field === "quantity" || field === "amount" ? Math.max(0, Number(value) || 0) : value;
    setItems(updated);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleShow = () => {
    // Check if userLabId exists before showing modal
    const storedUserLabId = localStorage.getItem('userLabId');
    if (!storedUserLabId) {
      alert("Please log in to create an invoice");
      return;
    }
    setUserLabId(storedUserLabId);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  // Handle shipper change → update cost + auto-check checkbox if price > 0
  const handleShipperChange = (e) => {
    const label = e.target.value;
    if (label === "") {
      // If empty option selected
      setSelectedShipping(null);
      setShippingCost("0.00");
      setAddShippingCost(false);
    } else {
      const found = shippingOptions.find(o => o.label === label);
      if (found) {
        setSelectedShipping(found);
        setShippingCost(found.price.toFixed(2));
        setAddShippingCost(found.price > 0);
      }
    }
  };

  const handleSave = async () => {
    // Validate userLabId exists
    if (!userLabId) {
      alert("Unable to create invoice: User identification not found. Please log in again.");
      return;
    }

    try {
      const procedureItems = caseData.procedures.map((proc, i) => ({
        description: proc.procedure,
        quantity: Number(units[i]) || 1,
        amount: Number(amounts[i]) || 0,
      }));

      const shippingItem = addShippingCost
        ? [{ description: "Shipping Cost", quantity: 1, amount: Number(shippingCost) || 0 }]
        : [];

      const invoiceData = {
        caseId: caseData?.caseNumber || caseId,
        accountId: caseData?.account_holder || caseId,
        addedBy: userLabId, // Add userLabId from localStorage
        trackNumber,
        shippingCost: addShippingCost ? parseFloat(shippingCost) || 0 : 0,
        shipper: selectedShipping?.label || "",
        rescheduleOption,
        invoiceNow,
        relatedCase,
        addShippingCost,
        items: [
          ...procedureItems,
          ...items.map(item => ({
            description: item.description,
            quantity: Number(item.quantity) || 1,
            amount: Number(item.amount) || 0,
          })),
          ...shippingItem,
        ],
        subtotal: Number(subtotal) || 0,
        taxes: Number(taxes) || 0,
        total: Number(total) || 0,
        invoiceDate,
        invoiceMessage,
        printAfterSave,
        status,
      };
      
      const res = await axios.post("https://hms.backend.hiragroupcloud.com/api/invoices/invoices", invoiceData);
      onInvoiceCreated?.(res.data);
      alert("Invoice created successfully!");
      handleClose();
    } catch (error) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div>
      <button
        style={{
          padding: "7px 10px",
          fontSize: "16px",
          cursor: "pointer",
          backgroundColor: "#004aad",
          color: "white",
          border: "none",
          borderRadius: "5px",
          marginBottom: "2px",
        }}
        onClick={handleShow}
      >
        Close/Invoice Case
      </button>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "10px",
              width: "100%",
              maxWidth: "750px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#004aad",
                padding: "10px 15px",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                borderTopLeftRadius: "5px",
                borderTopRightRadius: "5px",
              }}
            >
              <span>Close Case</span>
              <button
                style={{ background: "transparent", color: "white", border: "none", fontSize: "16px", cursor: "pointer" }}
                onClick={handleClose}
              >
                X
              </button>
            </div>
            <div style={{ padding: "20px" }}>
              {/* Shipping */}
              <h3 style={{ color: "#1E90FF", fontSize: "20px", padding: "10px" }}>Shipping Details</h3>
              <div style={{ backgroundColor: "#f4f4f4", padding: "15px", borderTop: "2px solid #004aad", borderRadius: "8px", marginBottom: "30px" }}>
                {["Reschedule", "Shipper", "Track #", "Shipping Cost"].map((label) => {
                  const isShipper = label === "Shipper";
                  const isShippingCost = label === "Shipping Cost";
                  return (
                    <div key={label} style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                      <label style={{ fontWeight: "600", width: "150px", color: "#333" }}>{label}</label>
                      {label === "Reschedule" ? (
                        <select value={rescheduleOption} onChange={e => setRescheduleOption(e.target.value)} style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}>
                          <option>Ship Today</option>
                          <option>Ship Next Business Day</option>
                          {deliveryDateOption && <option value={deliveryDateOption}>Delivery Date ({deliveryDateOption})</option>}
                        </select>
                      ) : isShipper ? (
                        <select
                          value={selectedShipping?.label || ""}
                          onChange={handleShipperChange}
                          disabled={shippingLoading}
                          style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}
                        >
                          <option value="">-- Select Shipping Service --</option>
                          {shippingLoading ? (
                            <option>Loading shipping options...</option>
                          ) : shippingOptions.length === 0 ? (
                            <option>No shipping options available</option>
                          ) : (
                            shippingOptions.map(opt => (
                              <option key={opt.id} value={opt.label}>
                                {opt.label}
                              </option>
                            ))
                          )}
                        </select>
                      ) : (
                        <input
                          type={isShippingCost ? "number" : "text"}
                          value={isShippingCost ? shippingCost : trackNumber}
                          onChange={e => {
                            const val = e.target.value;
                            if (isShippingCost) {
                              setShippingCost(val || "0.00");
                            } else {
                              setTrackNumber(val);
                            }
                          }}
                          min="0"
                          step="0.01"
                          style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Additional Actions */}
              <h3 style={{ color: "#1E90FF", fontSize: "20px", padding: "10px" }}>Additional Actions</h3>
              <div style={{ backgroundColor: "#f4f4f4", padding: "15px", borderTop: "2px solid #004aad", borderRadius: "8px", marginBottom: "30px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
                  <label><input type="checkbox" checked={invoiceNow} onChange={() => setInvoiceNow(!invoiceNow)} /> <strong>Invoice Now</strong></label>
                  <label><input type="checkbox" checked={relatedCase} onChange={() => setRelatedCase(!relatedCase)} /> <strong>Related Case</strong></label>
                </div>
                <label><input type="checkbox" checked={addShippingCost} onChange={() => setAddShippingCost(!addShippingCost)} /> <strong>Add Shipping Cost to Invoice</strong></label>
              </div>
              {/* Invoice Details */}
              <h3 style={{ color: "#1E90FF", fontSize: "20px", padding: "10px" }}>Invoice Details</h3>
              <div style={{ backgroundColor: "white", padding: "15px", borderTop: "2px solid #004aad", borderRadius: "8px" }}>
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ fontWeight: "600", width: "150px", color: "#333" }}>Invoice Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                {/* Procedures Table */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, marginBottom: "20px", boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
                  <thead>
                    <tr>
                      {["Units", "Description", "Amount"].map(h => (
                        <th key={h} style={{ padding: "12px 16px", backgroundColor: "#f4f4f4", color: "black", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #e0e0e0" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(caseData?.procedures || []).map((proc, i) => (
                      <tr key={proc._id} style={{ backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#fff" }}>
                        <td style={{ padding: "12px 16px" }}>
                          <input
                            type="number"
                            value={units[i] || proc.calculatedUnits}
                            onChange={e => handleUnitChange(i, e.target.value)}
                            min="1"
                            style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ padding: "12px 16px" }}>{proc.procedure}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <input
                            type="number"
                            value={amounts[i] || 0}
                            onChange={e => handleAmountChange(i, e.target.value)}
                            min="0"
                            style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Additional Items */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 10px", marginTop: "20px" }}>
                  <tbody>
                    <tr>
                      <td colSpan="4" style={{ textAlign: "left", fontWeight: "bold", fontSize: "16px", padding: "15px 10px", backgroundColor: "#f1f4f9", borderRadius: "8px" }}>
                        Additional Lines
                        <BsPlusSquareFill
                          style={{
                            color: "#004aad",
                            fontSize: "30px",
                            cursor: "pointer",
                            float: "right",
                            marginTop: "-5px"
                          }}
                          onClick={handleAddItem}
                        />
                      </td>
                    </tr>
                    {items.map((item, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f9f9f9", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", borderRadius: "6px" }}>
                        <td style={{ padding: "10px" }}>
                          <input
                            type="text"
                            value={item.description}
                            onChange={e => handleFieldChange(i, "description", e.target.value)}
                            placeholder="Item description"
                            style={{ padding: "8px", width: "130px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={e => handleFieldChange(i, "quantity", e.target.value)}
                            min="0"
                            placeholder="Qty"
                            style={{ padding: "8px", width: "90px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={e => handleFieldChange(i, "amount", e.target.value)}
                            min="0"
                            placeholder="Amount"
                            style={{ padding: "8px", width: "90px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ backgroundColor: "#004aad", borderRadius: "6px", height: "40px", width: "40px", display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }} onClick={() => handleRemoveItem(i)}>
                          <ImCross style={{ color: "white", fontSize: "16px" }} />
                        </td>
                      </tr>
                    ))}
                    {addShippingCost && (
                      <tr style={{ backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
                        <td style={{ padding: "10px" }}>Shipping Cost</td>
                        <td style={{ padding: "10px" }}>1</td>
                        <td style={{ padding: "10px" }}>{formatAmount(Number(shippingCost))}</td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* Totals */}
                <table style={{ width: "100%", marginTop: "20px" }}>
                  <tbody>
                    <tr><td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>Subtotal</td><td style={{ padding: "10px", textAlign: "right" }}>{formatAmount(subtotal)}</td></tr>
                    <tr><td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>Taxes</td><td style={{ padding: "10px", textAlign: "right" }}>{formatAmount(taxes)}</td></tr>
                    <tr><td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}><strong>Total</strong></td><td style={{ padding: "10px", textAlign: "right" }}><strong>{formatAmount(total)}</strong></td></tr>
                  </tbody>
                </table>
                <br />
                <div style={{ display: "flex", marginBottom: "15px" }}>
                  <label style={{ fontWeight: "bold", width: "50%", textAlign: "center" }}>Invoice Date</label>
                  <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} style={{ width: "30%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }} />
                </div>
                <div style={{ display: "flex", marginBottom: "15px" }}>
                  <label style={{ fontWeight: "bold", width: "50%", textAlign: "center" }}>Invoice Message</label>
                  <input type="text" value={invoiceMessage} onChange={e => setInvoiceMessage(e.target.value)} style={{ width: "30%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }} placeholder="Visible to customers" />
                </div>
                <label><input type="checkbox" checked={printAfterSave} onChange={() => setPrintAfterSave(!printAfterSave)} style={{ marginRight: "10px" }} /> Print after invoice is saved?</label>

                <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "20px" }}>
                  <button style={{ padding: "10px 20px", margin: "5px", backgroundColor: "#004aad", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }} onClick={handleClose}>Cancel</button>
                  <button style={{ padding: "10px 20px", margin: "5px", backgroundColor: "#004aad", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }} onClick={handleSave}>Save Invoice</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CloseInvoiceCase;